import { useState, useEffect } from 'react';
import { X, Download, Smartphone, Share, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Detect device type
const getDeviceType = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios';
  }
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  return 'desktop';
};

// Check if app is already installed
const isAppInstalled = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
};

export default function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [deviceType] = useState(() => getDeviceType());
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem('pwa-prompt-dismissed') === 'true' || isAppInstalled();
  });

  useEffect(() => {
    if (dismissed || isAppInstalled()) {
      return;
    }

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a delay
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show manual instructions after delay
    if (getDeviceType() === 'ios') {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [dismissed]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  const handleRemindLater = () => {
    setShowPrompt(false);
    // Show again next session
  };

  if (!showPrompt || dismissed || isAppInstalled()) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in slide-in-from-bottom duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1E40AF] to-[#1E3A8A] p-6 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <img
                src="/icons/icon-96x96.png"
                alt="ARTEKNOS"
                className="w-16 h-16 rounded-xl shadow-lg"
              />
              <div>
                <h3 className="text-xl font-bold">ARTEKNOS</h3>
                <p className="text-sm text-blue-200">Livraison Express</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            Installer l&apos;application
          </h4>
          <p className="text-gray-600 text-sm mb-4">
            Accédez rapidement à ARTEKNOS depuis votre écran d&apos;accueil.
            Aucun téléchargement requis !
          </p>

          {/* Benefits */}
          <div className="space-y-2 mb-6">
            {[
              'Accès rapide depuis l\'écran d\'accueil',
              'Notifications en temps réel',
              'Fonctionne hors ligne',
              'Expérience native'
            ].map((benefit) => (
              <div key={benefit} className="flex items-center gap-2 text-sm text-gray-700">
                <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">✓</span>
                {benefit}
              </div>
            ))}
          </div>

          {/* Device-specific instructions */}
          {deviceType === 'ios' ? (
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <p className="text-sm font-medium text-gray-900 mb-3">
                Comment installer sur iPhone/iPad :
              </p>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#1E40AF] text-white flex items-center justify-center font-bold">1</div>
                  <div className="flex items-center gap-2">
                    Appuyez sur <Share className="w-5 h-5 text-[#1E40AF]" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#1E40AF] text-white flex items-center justify-center font-bold">2</div>
                  <div className="flex items-center gap-2">
                    Sélectionnez <span className="font-medium">&quot;Sur l&apos;écran d&apos;accueil&quot;</span> <Plus className="w-5 h-5" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#1E40AF] text-white flex items-center justify-center font-bold">3</div>
                  <span>Appuyez sur <span className="font-medium">&quot;Ajouter&quot;</span></span>
                </div>
              </div>
            </div>
          ) : deviceType === 'android' && deferredPrompt ? (
            <Button
              onClick={handleInstall}
              className="w-full bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-full py-6 font-semibold mb-3"
            >
              <Download className="w-5 h-5 mr-2" />
              Installer l&apos;application
            </Button>
          ) : (
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <p className="text-sm font-medium text-gray-900 mb-2">
                Installation sur ordinateur :
              </p>
              <p className="text-sm text-gray-600">
                Cliquez sur l&apos;icône d&apos;installation dans la barre d&apos;adresse de votre navigateur.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 rounded-full"
              onClick={handleRemindLater}
            >
              Plus tard
            </Button>
            {deviceType === 'ios' && (
              <Button
                className="flex-1 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-full"
                onClick={handleDismiss}
              >
                J&apos;ai compris
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Export hook for manual trigger
export const usePWAInstall = () => {
  const [canInstall, setCanInstall] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setCanInstall(false);
      return outcome === 'accepted';
    }
    return false;
  };

  return { canInstall, install, isInstalled: isAppInstalled() };
};
